import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';

// Remembering where a reader was scrolled to, shared by the notepad and the
// full-screen preview. It looks like a one-liner and is not; both of the
// problems below cost a failed live run before they were understood, so the
// logic lives in ONE place rather than being written out twice.
//
// Positions are held IN MEMORY, not in storage, and so last exactly as long as
// the loaded page does: moving between note pages keeps your place, and a
// refresh deliberately starts you at the top again. A reload is how people ask
// for a clean slate, and a remembered offset into a document that may have been
// edited elsewhere since is worse than the top of it.
//
// A plain module-level Map, which also means no storage to be unavailable, to
// throw in a private window, or to fill up.

/** How long after a restore starts we keep re-applying it. */
const RESTORE_WINDOW_MS = 1200;
/** Re-apply cadence inside that window. */
const RETRY_MS = 80;
/** Throttle on writes; the tracked value itself updates unthrottled. */
const WRITE_THROTTLE_MS = 150;

/** Cleared by a reload, because the module itself is. */
const positions = new Map<string, number>();

function read(prefix: string, key: string): number {
  const n = positions.get(prefix + key) ?? 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function write(prefix: string, key: string, top: number): void {
  positions.set(prefix + key, Math.round(top));
}

/**
 * Restores `el`'s scroll position for `key`, and records it as the reader
 * scrolls. Changing `key` saves the outgoing position before restoring the
 * incoming one, so the same element can serve several documents.
 *
 * Returns `resettle`, which restarts the restore window — call it when the
 * element's CONTENT has been replaced asynchronously, since an offset applied
 * while the document is still empty is clamped away.
 */
export function useScrollMemory(
  elRef: RefObject<HTMLElement | null>,
  key: string | undefined,
  prefix: string,
): { resettle: () => void } {
  const resettleRef = useRef<() => void>(() => { /* no-op until mounted */ });

  useEffect(() => {
    const el = elRef.current;
    if (!key || !el) return;

    const target = read(prefix, key);
    // The position we BELIEVE we are at, tracked rather than read back at
    // teardown. React runs effect cleanups in declaration order, and the
    // editor's `destroy()` runs before this one — it empties the DOM, so a
    // cleanup that read `el.scrollTop` would always see 0 and save that over
    // the real position.
    let last = target;
    let settling = false;
    let ticker: ReturnType<typeof setInterval> | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const apply = (): void => {
      if (!settling || !el.isConnected) return;
      // A document that is still short (webfont reflow, content not yet
      // swapped in) clamps the assignment, which is why this runs repeatedly
      // rather than once.
      if (Math.abs(el.scrollTop - target) > 1) el.scrollTop = target;
    };

    const stopSettling = (): void => {
      settling = false;
      if (ticker) { clearInterval(ticker); ticker = null; }
      if (timer) { clearTimeout(timer); timer = null; }
    };

    const startSettling = (): void => {
      stopSettling();
      if (target <= 0) {
        // A document with no remembered position opens at its TOP, and that
        // has to be asserted rather than assumed. The preview reuses ONE
        // scroll container for every tab, so without this an unvisited note
        // inherits the offset of the one you were just reading and opens
        // already scrolled. (No settling loop is needed: reflow can never push
        // a document away from 0, so one assignment is enough.)
        if (el.isConnected) el.scrollTop = 0;
        return;
      }
      settling = true;
      apply();
      ticker = setInterval(apply, RETRY_MS);
      timer = setTimeout(stopSettling, RESTORE_WINDOW_MS);
    };

    resettleRef.current = startSettling;
    startSettling();
    const raf = requestAnimationFrame(apply);
    // `document.fonts` is absent in older WebViews; the restore still works
    // there, it just relies on the ticker.
    void document.fonts?.ready.then(() => requestAnimationFrame(apply)).catch(() => undefined);

    // Any deliberate move by the reader beats a pending re-apply.
    const intents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    for (const ev of intents) el.addEventListener(ev, stopSettling, { passive: true });

    let throttle: ReturnType<typeof setTimeout> | null = null;
    const onScroll = (): void => {
      // While settling the only thing moving the element is us, and a clamped
      // target must never be recorded as the reader's position.
      if (settling) return;
      last = el.scrollTop;
      if (throttle) return;
      throttle = setTimeout(() => { throttle = null; write(prefix, key, last); }, WRITE_THROTTLE_MS);
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      stopSettling();
      if (throttle) clearTimeout(throttle);
      el.removeEventListener('scroll', onScroll);
      for (const ev of intents) el.removeEventListener(ev, stopSettling);
      resettleRef.current = () => { /* unmounted */ };
      // The moment that matters most: a throttled write may still be pending,
      // and leaving a note page fires no unload.
      write(prefix, key, last);
    };
  }, [elRef, key, prefix]);

  // Stable identity, deliberately. Callers put `resettle` in an effect's
  // dependency list; a fresh function each render would re-run that effect on
  // every render, restarting the restore window continuously and pinning the
  // reader to the stored offset instead of letting them scroll.
  const resettle = useCallback(() => { resettleRef.current(); }, []);
  return useMemo(() => ({ resettle }), [resettle]);
}

/** Key namespaces within the in-memory map. Separate because the two surfaces
 *  lay text out differently — the same offset is not the same place in both. */
export const EDITOR_SCROLL_PREFIX = 'editor:';
export const PREVIEW_SCROLL_PREFIX = 'preview:';
